import pluralize from '../util/pluralize'
import assign from 'lodash/assign'
import clone from 'lodash/clone'
import compact from 'lodash/compact'
import find from 'lodash/find'
import debounce from 'lodash/debounce'
import keys from 'lodash/keys'
import map from 'lodash/map'
import max from 'lodash/max'
import pick from 'lodash/pick'
import pickBy from 'lodash/pickBy'
import validator from 'validator'
import demoData from './demoData'
import routes from '../routes'
import 'whatwg-fetch' // Polyfill for browsers that don't have `fetch`


// Figure out if the UI was loaded from the API server, based on the hostname
var ON_API_SERVER = (/^(localhost|127\.0\.0\.1|::1|\[::1\]|(ordering|local)\.coolfront\.com|.*herokuapp\.com|.*azurecontainerapps\.io|.*azurewebsites\.net)$/).test(window.location.hostname)

// If the UI is loaded from somewhere else, point it at the API server (requires CORS)
const API_BASE_URL = ON_API_SERVER ? '' : "https://ordering.coolfront.com"

// Find out if some sent us here
const REFERRER = getQueryParam('from')
const BOOK_ID = getQueryParam('watch')
const BRAND = getQueryParam('brand')
const PRICING_MODE = getQueryParam('pricing_mode')
const READ_ONLY = !!BOOK_ID


// Check if smoothing scrolling is supported by the browser
const SMOOTH_SCROLL = 'scrollBehavior' in document.documentElement.style


// TODO: Break up this file

const DEBUG = 0
const INFO  = 1
const WARN  = 2
const ERROR = 4

let LOGGING_LEVEL = INFO
if (window.location.hostname.indexOf('local') === 0) LOGGING_LEVEL = DEBUG
if (READ_ONLY) LOGGING_LEVEL = WARN

function log(msg)   { if (LOGGING_LEVEL <= DEBUG) console.log(msg) }
function info(msg)  { if (LOGGING_LEVEL <= INFO)  console.info(msg) }
function warn(msg)  { if (LOGGING_LEVEL <= WARN)  console.warn(msg) }
function error(msg) { if (LOGGING_LEVEL <= ERROR) console.error(msg) }



export default class Store {

  constructor (renderCallback) {
    this.renderCallback = renderCallback

    // Expose some "actions" that can be called by the app
    this.actions = {}

    const addAction = (methodName) => {
      const self = this
      if (typeof self[methodName] !== 'function') throw new Error(`Cannot add action called '${methodName}' -- method not found`)
      if (READ_ONLY) {
        self.actions[methodName] = function () {
          warn('Ignoring action ' + methodName + ' in READONLY mode')
        }
        return // Don't make any actions in read-only mode
      }
      self.actions[methodName] = function () {
        let args = Array.prototype.slice.call(arguments)
        if (args[0] && args[0].preventDefault) args = args.slice(1) // Remove events
        if (args[0] && args[0].id) args[0] = {id: args[0].id} // Just show the order id
        try {
          args = JSON.stringify(args)
        } catch (e) {
          args = 'UNKNOWN'
        }
        log('Called action ' + methodName + ' ' + args)
        self[methodName].apply(self, arguments)
      }
    }

    addAction('closeBookConfig')
    addAction('openBookConfig')
    addAction('flagOrderAsConfigured')
    addAction('gotoBookConfigStep')
    addAction('gotoNextPage')
    addAction('gotoPrevPage')
    addAction('handleCreditInfoChange')
    addAction('hideTopLevelError')
    addAction('showTopLevelErrors')
    addAction('loadDemoData')
    addAction('placeOrder')
    addAction('resetDemo')
    addAction('setAgreementToTerms')
    addAction('setPartsMarkupOption')
    addAction('setPricingFormat')
    addAction('setNumberOfPrintedBooks')
    addAction('setNumberOfLicenses')
    addAction('setBookSelected')
    addAction('setSoftwareName')
    addAction('setTopLevelError')
    addAction('startNewOrder')
    addAction('switchToProcessingStatus')
    addAction('toggleAdminMode')
    addAction('toggleRepairGroup')
    addAction('toggleShareAddressField')
    addAction('updateBillingAddress')
    addAction('updateContact')
    addAction('updateElectronicSignature')
    addAction('updateNumberOfLicensesOnAllBooks')
    addAction('updateOrder')
    addAction('updateOvertimeDiscountAmount')
    addAction('updateRegularDiscountAmount')
    addAction('updateShippingAddress')

    this._data = {
      orders: [],
    }

    if (this.hasExistingOrder()) {
      this.tryToReloadExistingOrder()
    } else {
      this.resetData()
      this.initializeRouting()
    }

    this.state = this.generateState()

    window.onhashchange = this.onHashChange
  }


  //============================================================================


  initializeRouting = () => {
    if (window.location.hash) {
      this.onHashChange()
    } else {
      this.navigateTo(routes.get(0).path)
    }
  }


  setPath = (newPath) => {
    this._data.path = newPath
    this.render()
  }


  closeBookConfig = () => {
    this._data.previousBookId = this._data.currentBookId
    this._data.currentBookId = -1
    this.render()
  }


  openBookConfig = (bookId) => {
    // Stay on the same step, when reopening the same book config
    if (bookId !== this._data.previousBookId) {
      this._data.bookConfigStep = 0
    }
    this._data.currentBookId = bookId
    this.render()
  }


  gotoBookConfigStep = (step) => {
    this._data.bookConfigStep = step
    this.render()
  }


  gotoPrevPage = () => {
    var i = routes.indexForPath(this._data.path)
    this.navigateTo(routes.get(i - 1).path)
  }


  gotoNextPage = () => {

    // Don't navigate if there are errors on the current page
    this.showTopLevelErrors()
    if (this._data.showErrors) return

    // Don't navigate past the last page
    var i = routes.indexForPath(this._data.path)
    if (routes.length() === i + 1) return

    this.navigateTo(routes.get(i + 1).path)
  }


  currentPageData = () => {
    var i = routes.indexForPath(this._data.path)
    return this._data[routes.get(i).data] || {}
  }


  errorsOnCurrentPage = () => {
    var i = routes.indexForPath(this._data.path)
    if (i < 0) return 0

    const data = this._data[routes.get(i).data] || {}

    return data._errors
  }


  hideTopLevelError = () => {
    this._data.topLevelError = ''
    this.render()
  }


  // Display any top-level error messages
  showTopLevelErrors = () => {
    const data = this.currentPageData()
    const errorCount = keys(data._errors).length

    this._data.showErrors = !!errorCount
    this._data.errorCount = errorCount

    var errorMessages = []

    if (errorCount) {
      keys(data._errors).forEach(fieldName => {
        let msg = data._errors[fieldName]
        if (fieldName !== '_root') msg = [humanize(fieldName), msg].join(' ')
        errorMessages.push(msg)
      })
      this._data.topLevelError = errorMessages.join("\n")
    }

    this.render()
  }


  setTopLevelError = (msg) => {
    this._data.topLevelError = msg
    this.render()
  }


  navigateTo = (newPath) => {
    info('navigateTo ' + newPath)
    if (newPath === '/address') this.logConversion()
    window.location.hash = '#!' + newPath
  }


  logConversion() {
    if (!this._conversionLogged) {
      // Event snippet for Lead Created conversion page
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { 'send_to': 'AW-993821867/n05cCN7z8aoBEKuJ8tkD' })
      }
      this._conversionLogged = true
    }
  }


  onHashChange = () => {
    if (READ_ONLY) return

    let newPath = window.location.hash.substr(2)

    if (newPath === '/order') this.recalculate()
    if (newPath === '/completed') {
      newPath = '/order'
      this._data.orderStatus = 'completed'
    }

    if (routes.indexForPath(newPath) < 0) {
      warn(`Bad path: '${newPath}' -- Redirecting to default route`)
      this.navigateTo(routes.get(0).path)
    } else {
      this.setPath(newPath)
      this.scrollTop()
      this.render()
    }
  }


  scrollTop = () => {
    // Handle old browsers that don't have SMOOTH scrolling support
    if (!SMOOTH_SCROLL) return window.scrollTo(0, 0)

    window.scrollTo({left: 0, top: 0, behavior: 'smooth'})
  }


  resetData = () => {
    // Set some defaults
    this._data = {}
    this._data.pricingMode = 'Legacy'
    this._data.brand = 'coolfront'
    this._data.orderStatus = 'active'
    this._data.contact = {}
    this._data.software = {}
    this._data.billingAddress = { country: 'US' }
    this._data.shippingAddress = { country: 'US' }
    this._data.terms = { electronicSignature: '', agreedToTerms: false }
    this._data.pricing = {}
    this._data.payment = {}
    this._data.credit = {}

    if (typeof REFERRER === 'string') {
      this._data.software.name = REFERRER
      this.updateReferrer()
    }

    // Populate the list of book options
    this._data.orders = [
      new Order('HVC', 'Commercial'),
      new Order('HVC', 'Residential'),
      new Order('PLB', 'Residential'),
      new Order('ELE', 'Residential'),
    ]

    this.applyChanges()
  }


  loadDemoData = () => {
    // Preserve the path (nice for reloading in dev)
    this.rehydrateData(demoData, {keepPath: true})
    delete this._data.id
    delete this._data.ids
    this._data.demo = true
    this._data.orderStatus = 'active'
    this.render()
  }


  handleCreditInfoChange = (data) => {
    // Use `pick` to make sure we only get the PCI compliant data
    let safeFields = pick(data, ['cardName', 'cardMonth', 'cardYear', 'cardNumValid'])
    if (safeFields.cardMonth) {
      // Try to help by added the leading zero on single digit months
      if (safeFields.cardMonth.length === 1) safeFields.cardMonth = '0' + safeFields.cardMonth
      if (safeFields.cardMonth.length > 2 && safeFields.cardMonth[0] === '0') {
        safeFields.cardMonth = safeFields.cardMonth.substr(1)
      }
    }
    assign(this._data.credit, safeFields)
    this.render()
  }


  rehydrateData(data, opts) {
    const currentPath = this._data.path

    assign(this._data, data)

    // Wrap the order data with the Order class
    this._data.orders = this._data.orders.map(o => {
      let ord = new Order()
      assign(ord, o)
      return ord
    })

    if (opts && opts.keepPath) {
      this._data.path = currentPath
    } else {
      window.location.hash = '#!' + data.path
    }
  }


  updateOvertimeDiscountAmount = (order, discountAmount) => {
    order = this.findOrder(order.id)
    order.overtimeDiscountAmount = discountAmount
    this.render()
  }



  updateRegularDiscountAmount = (order, discountAmount) => {
    order = this.findOrder(order.id)
    order.regularDiscountAmount = discountAmount

    // For non-legacy pricing, setting the regular discount amount, also sets the
    // overtime discount (which is hidden in the UI)
    const isLegacyPricing = (this._data.pricingMode && this._data.pricingMode === 'Legacy')
    if (!isLegacyPricing) order.overtimeDiscountAmount = discountAmount

    this.render()
  }

  setNumberOfPrintedBooks = (order, newCount) => {
    order = this.findOrder(order.id)
    order.numberOfPrintedBooks = newCount
    if (order.numberOfPrintedBooks > order.numberOfLicenses) {
      order.numberOfLicenses = order.numberOfPrintedBooks
    }
    this.render()
  }

  setNumberOfLicenses = (order, newCount) => {
    order = this.findOrder(order.id)
    order.numberOfLicenses = newCount
    if (order.numberOfPrintedBooks > order.numberOfLicenses) {
      order.numberOfPrintedBooks = order.numberOfLicenses
    }
    this.render()
  }


  setBookSelected = (order, selected) => {
    order = this.findOrder(order.id)
    order.bookSelected = selected
    this.render()
  }


  tryToReloadExistingOrder = () => {
    const id = this.getExistingOrderId()
    if (!id) return

    info('Trying to load existing order ' + id)
    if (!READ_ONLY) this._data.loading = true
    this.render()

    this.apiGet('/api/book-order/' + id)
    .then(res => {
      info('Loaded existing order ' + id)
      this.rehydrateData(res, {keepPath: !READ_ONLY})
      this.removeCetaBook()
      this._data.loading = false
      this.initializeRouting()
      this.applyChanges()
      this.render()
    })
    .catch(err => {
      if (err.status === 404) {
        warn(`Cannot reload order ${id}. Starting a fresh one.`)
      } else {
        warn(`Unexpected error when reloading existing order. Starting a fresh one.`)
        error(err)
      }
      this._data.loading = false
      this.startNewOrder()
    })
    .then(() => this.render())
    .then(() => {
      if (READ_ONLY) setTimeout(() => {
        this.tryToReloadExistingOrder()
      }, 1000);
    })
  }


  toggleAdminMode = () => {
    log('adminMode')
    this._data.adminMode = !this._data.adminMode
    this.render()
  }


  resetDemo = () => {
    log('resetDemo')
    this._data.adminMode = false
    this.loadDemoData()
    this.navigateTo('/')
    this.render()
  }


  startNewOrder = () => {
    log('startNewOrder')
    this._data.adminMode = false
    if (window.localStorage) localStorage.removeItem('bookOrderId')
    this.resetData()
    this.navigateTo('/')
  }


  hasExistingOrder = () => !!this.getExistingOrderId()


  switchToErrorState = (err) => {
    this._data.lastErrorMessage = err.message
    this._data.orderStatus = 'error'
  }


  switchToProcessingStatus = () => {
    this._data.orderStatus = 'processing'
    this.render()
  }


  getExistingOrderId = () => {
    if (BOOK_ID) return BOOK_ID
    return window.localStorage && localStorage.getItem('bookOrderId')
  }


  placeOrder = () => {
    if (this.canOrderWithoutPayment()) {

      this.switchToProcessingStatus()

      this.apiPost('/api/book-order/process-free', this._data)
      .then(res => {
        this._data.orderStatus = 'completed'
        this.saveOrder()
        this.render()
      })
      .catch(err => {
        this.switchToErrorState(err)
        error(err)
        this.render()
      })
    } else {
      this.switchToProcessingStatus()

      this.apiPost('/api/book-order/process-no-payment', this._data)
      .then(res => {
        this._data.orderStatus = 'completed'
        this.saveOrder()
        this.render()
      })
      .catch(err => {
        this.switchToErrorState(err)
        error(err)
        this.render()
      })
    }
  }


  canOrderWithoutPayment () {
    if (this._data.path !== '/order') return
    if (this._data.pricing.recalculating) return
    if (this._data.pricing.activationFeeTotal) return

    return true
  }


  showPaymentErrorMessage = () => {
    this._data.orderStatus = 'error'
    this.render()
  }


  // Ask the server to recalculate all the fees and taxes
  recalculate = () => {
    this._data.pricing.recalculating = true

    this.render()

    this.apiPost('/api/book-order/recalc', this._data)
    .then(res => {
      this._data.pricing = res.pricing
      this._data.payment = res.payment
      this._data.ids     = res.ids
      this.render()
    })
    .catch(err => {
      this.switchToErrorState(err)
      error(err)
      this.render()
    })
  }

  setPartsMarkupOption = (order, partsMarkupOption) => {
    order = this.findOrder(order.id)
    order.partsMarkupOption = partsMarkupOption
    this.render()
  }

  setSoftwareName = (name) => {
    this._data.software = this._data.software || {}

    // When switching software reset the book config back to the first step
    if (name !== this._data.software.name) {
      this._data.bookConfigStep = 0
    }

    this._data.software.name = name
    this.updateReferrer()
    const pricingMode = (typeof name === 'string' && name.toLowerCase() === 'fieldedge') ? 'Safe' : 'Legacy'

    if (this._data.pricingMode !== pricingMode) {
      this._data.pricingMode = pricingMode
      this.updatePricingMode(pricingMode)
    }

    this.render()
  }


  applyChanges = () => {
    const brand = BRAND || this._data.brand || 'coolfront'

    // Ensure to default pricing mode to 'Safe' when only 'brand' is specificed and it's 'fieldedge' for backwards compatibility.
    // However, if for some reason there is 'older' pricingMode data that is already set use that if just the 'fieldedge' brand is passed in.
    const pricingMode = capitalize(PRICING_MODE || ((brand && brand === 'fieldedge' && this._data.pricingMode === 'Legacy') ? 'Safe' : this._data.pricingMode) || 'Legacy')

    let hasBrandChanged = this._data.brand !== brand
    let hasPricingModeChanged = this._data.pricingMode !== pricingMode

    // When using fieldedge branding, the software screen is hidden
    if (pricingMode === 'Legacy') {
      routes.restorePath('/software')
    } else {
      routes.removePath('/software')
    }

    if (hasBrandChanged) {
      // When branded for fieldedge, automatically pick that software
      if (brand === 'fieldedge') this.setSoftwareName(brand)
    }

    if (hasPricingModeChanged) {
      this.updatePricingMode(pricingMode)
    }

    this._data.brand = brand
    this._data.pricingMode = pricingMode

    // When the brand or pricingMode changes, jump back to the start of the process
    if (hasBrandChanged || hasPricingModeChanged) this.navigateTo(routes.get(0).path)

  }


  removeCetaBook = () => {
    this._data.orders = this._data.orders.filter(o => o.tradeCode !== 'CET')
  }


  updatePricingMode = (pricingMode) => {

    if (pricingMode === 'Legacy') {
      // Non-fieldedge orders can only have a max of 3 oems
      this._data.orders.forEach(o => {
        o.oems = o.oems.slice(0, 3)
      })

    } else { // 'Safe', 'Simplified'
      var maxLicenseCount = max(map(this._data.orders, 'numberOfLicenses'))

      this._data.orders.forEach(o => {
        o.bookSelected           = true
        o.pricingFormat          = 'Agreement Reg-OT'
        o.partsTaxCalculation    = 'Tax Sale'
        o.partsMarkupOption      = 'Option 1' // Aggressive
        o.partsMarkupTable       = ''
        o.overtimeDiscountAmount = o.regularDiscountAmount
        o.regularDiscountType    = '%'
        o.overtimeDiscountType   = '%'
        o.laborTaxRate           = '0'
        o.partsTaxRate           = '0'
        o.numberOfLicenses       = maxLicenseCount
        o.numberOfPrintedBooks   = 0
        o.displayBarcodes        = false
        o.roundedPrices          = false
        o.oems                   = o.tradeCode === 'HVC' ? map(OEMS, 'code') : []

        // Use fieldedge column naming
        o.column1Title = "Regular"
        o.column2Title = "Agreement"
        o.column3Title = "After\nHours"
        o.column4Title = "After Hours\nAgreement"

        o.setAllOptionalRepairGroups(true)
      })
    }

    // Mark all orders as unconfigured, so they will go through them again
    this._data.orders.forEach(o => {
      o.configured = false
    })
  }


  // Set the `referrer` based on the software picked
  updateReferrer = () => {
    if (typeof this._data.software.name === 'string') {
      const n = this._data.software.name.toLowerCase()
      if (n === 'esc') {
        this._data.referrer = 'desco'
      } else if (n === 'fieldedge') {
        this._data.referrer = 'FieldEdge'
      } else {
        this._data.referrer = ''
      }
    }
  }

  updateElectronicSignature = (name) => {
    this._data.terms.electronicSignature = name
    this.render()
  }

  setPricingFormat = (order, newPricingFormat) => {
    order = this.findOrder(order.id)
    const originalFormat = order.pricingFormat
    if (newPricingFormat !== originalFormat) {
      order.pricingFormat = newPricingFormat
      order.setDefaultColumns()
      this.render()
    }
  }

  setAgreementToTerms = (agreed) => {
    this._data.terms.agreedToTerms = !!agreed
    this.render()
  }

  updateContact = (data) => {
    assign(this._data.contact, data)

    this._data.terms.electronicSignature = [
      this._data.contact.firstName,
      this._data.contact.lastName,
    ].join(' ')

    this.render()
  }

  updateBillingAddress = (data) => {
    this.updateAddress(this._data.billingAddress, data)
    const {companyName} = this._data.billingAddress
    this._data.orders.forEach(o => o.printedName = companyName)
    this.render()
  }

  updateNumberOfLicensesOnAllBooks = (count) => {
    this._data.orders.forEach(o => o.numberOfLicenses = count)
    this.render()
  }

  updateShippingAddress = (data) => {
    this.updateAddress(this._data.shippingAddress, data)
    this.render()
  }

  flagOrderAsConfigured = (order) => {
    order.configured = true

    const configuredOrders = this._data.orders.filter(o => o.configured)
    const isFirstConfig = configuredOrders.length === 1

    // Copy configuration of the first book, into the other (minus labor rates)
    if (isFirstConfig) {
      this._data.orders.forEach(o => {
        if (o.configured) return

        o.pricingFormat          = order.pricingFormat

        o.column1Highlighted     = order.column1Highlighted
        o.column2Highlighted     = order.column2Highlighted
        o.column3Highlighted     = order.column3Highlighted
        o.column4Highlighted     = order.column4Highlighted

        o.column1Printed         = order.column1Printed
        o.column2Printed         = order.column2Printed
        o.column3Printed         = order.column3Printed
        o.column4Printed         = order.column4Printed

        o.column1Title           = order.column1Title
        o.column2Title           = order.column2Title
        o.column3Title           = order.column3Title
        o.column4Title           = order.column4Title

        o.partsTaxCalculation    = order.partsTaxCalculation
        o.partsMarkupOption      = order.partsMarkupOption
        o.partsMarkupTable       = order.partsMarkupTable
        o.regularFlatAdd         = order.regularFlatAdd
        o.overtimeFlatAdd        = order.overtimeFlatAdd

        // Do NOT copy in the labor rates (regularLaborRate, overtimeLaborRate)

        o.regularDiscountAmount  = order.regularDiscountAmount
        o.regularDiscountType    = order.regularDiscountType
        o.overtimeDiscountAmount = order.overtimeDiscountAmount
        o.overtimeDiscountType   = order.overtimeDiscountType

        o.laborTaxRate           = order.laborTaxRate
        o.partsTaxRate           = order.partsTaxRate
        o.numberOfPrintedBooks   = order.numberOfPrintedBooks
        o.numberOfLicenses       = order.numberOfLicenses

        o.displayBarcodes        = order.displayBarcodes
        o.roundedPrices          = order.roundedPrices
        o.displayTime            = order.displayTime

        if (o.tradeCode === 'HVC') o.oems = clone(order.oems)

        o.printedGroupCodes = clone(order.printedGroupCodes)

        o.specialRequests = order.specialRequests
      })
    }
  }

  updateOrder = (order, data) => {
    order = this.findOrder(order.id)
    assign(order, data)
    this.render()
  }

  toggleShareAddressField = () => {
    this._data.useBillingAddress = !this._data.useBillingAddress
    if (this._data.useBillingAddress) {
      this._data.shippingAddress = clone(this._data.billingAddress)
    }
    this.render()
  }

  toggleRepairGroup = (order, code) => {
    order = this.findOrder(order.id)
    order.setRepairGroupPrintFlag(code, !order.isPrintingGroupCode(code))
    this.render()
  }


  generateState = () => {
    let state = this._data
    this.validate(state)

    state.currentBook = null

    state.orders.forEach(order => {
      const originalOrder = this.findOrder(order.id)
      order.title = originalOrder.getTitle()
      order.tradeName = originalOrder.getTitle()
      order.isComplete = !keys(order._errors).length && order.configured

      if (order.id === state.currentBookId) {
        state.currentBook = order
      }
    })

    state.canOrderWithoutPayment = this.canOrderWithoutPayment()
    state.readOnly = READ_ONLY

    return state
  }


  saveOrder = debounce(() => {
    if (READ_ONLY) return Promise.resolve()

    // Avoid saving while other API calls are firing
    if (this._apiBusy) {
      setTimeout(this.saveOrder, 1000)
      return
    }
    this.apiPost('/api/book-order/save', this._data)
    .then(res => {
      if (!this._data.id) {
        this._data.id = res.id
        this._data.appVersion = res.appVersion
        if (res.id && window.localStorage) localStorage.setItem('bookOrderId', res.id)
        log('Saved order to server ' + res.id)
      }
    })
    .catch(err => {
      // If this book order isn't found on the server, clear the `id` so the
      // server will create a new record and assign a new `id`.
      if (err.status === 404) {
        warn(`Save failed on order ${this._data.id} -- clearing id to create a new record`)
        this._data.id = ''
        this.saveOrder()
      } else {
        this.switchToErrorState(err)
        this.render()
      }
    })
  }, 1000 * 1) // second between requests to save the data on the server


  findOrder = (id) => {
    return find(this._data.orders, {id: id})
  }


  updateAddress = (address, data) => {
    assign(address, data)
  }


  apiGet = (url) => this.apiRequest('GET', url)


  apiPost = (url, data) => this.apiRequest('POST', url, data)


  apiRequest = (method, url, data) => {
    if (READ_ONLY && method === 'POST') {
      // Don't update server read-only mode
      warn('Rejecting apiRequest in READONLY mode')
      return Promise.reject(new Error('No apiPost allowed in READONLY mode'))
    }

    let status = null
    log(`Api request: ${method} ${url} started`)
    this._apiBusy = true
    return window.fetch(API_BASE_URL + url,
      {
        method:  method,
        mode:    'cors',
        headers: {
          'Accept':       'application/json',
          'Content-Type': 'application/json',
        },
        body:    data ? JSON.stringify(data) : null,
      }
    )
    .then(res => {
      log(`Api request: ${method} ${url} completed`)
      status = res.status
      return res.json()
    })
    .then(data => {
      if (status === 200) return data
      warn(`Error during ${method} ${url} request...`)
      warn(JSON.stringify(data, null, 2))
      let apiError = new Error(data.error)
      apiError.status = status
      throw apiError
    })
    .then(data => {
      this._apiBusy = false
      return data
    })
    .catch(err => {
      this._apiBusy = false
      throw err
    })
  }


  render = () => {
    this.state = this.generateState()

    this.saveOrder()

    if (typeof this.renderCallback === 'function') {
      this.renderCallback()
    }
  }


  // Check each object and add `_errors` if there are any validation issues
  validate = (state) => {
    const pricingMode = state.pricingMode

    runValidation(state.contact,         validateContact)
    runValidation(state.billingAddress,  validateAddress)
    runValidation(state.shippingAddress, validateAddress)
    runValidation(state.software,        validateSoftware)
    runValidation(state.orders,          validateOrders, {pricingMode: pricingMode})
    runValidation(state.terms,           validateTerms)
    runValidation(state.credit,          validateCreditInfo)

    state.pageErrors = this.errorsOnCurrentPage()
    state.pageErrorCount = keys(state.pageErrors).length
  }

}


function runValidation(obj, validator, opts){
  if (!obj) return
  obj._errors = {} // Reset the error messages
  validator(obj, opts)   // Run the validation (which populates _errors)
  obj._errors = pickBy(obj._errors) // Remove any empty errors
}


function validateAddress(address) {
  validateRequiredField(address, 'companyName')
  validateRequiredField(address, 'address1')
  validateRequiredField(address, 'city')
  validateStateField(address, 'state')
  validateRequiredField(address, 'zip')
  validateCountryField(address, 'country')
  validatePhone(address, 'phone')
}


function validateSoftware(software) {
  validateRoot(
    software,
    typeof software.name === 'string',
    'Please pick the software you use'
  ) &&
  validateRoot(
    software,
    software.name,
    'Please type the name of your software'
  )
}


function validateContact(contact) {
  validateRequiredField(contact, 'firstName')
  validateRequiredField(contact, 'lastName')
  validateRequiredField(contact, 'jobTitle')
  validatePhone(contact, 'phone')
  validateRequiredField(contact, 'email')
  validateEmail(contact, 'email')
}


function validateOrders(orders, opts) {
  const booksSelected = orders.filter(o => o.bookSelected)
  validateRoot(orders, booksSelected.length, 'Please add at least one book')

  booksSelected.forEach(order => { runValidation(order, validateOrder, opts) })

  if (booksSelected.length) {
    const booksConfigured = booksSelected.filter(o => {
      const hasErrors = !!keys(o._errors).length
      return (!hasErrors && o.configured)
    })
    const unconfiguredCount = booksSelected.length - booksConfigured.length
    validateRoot(
      orders,
      !unconfiguredCount,
      `${unconfiguredCount} ${pluralize(unconfiguredCount, 'book')} still ${pluralize(unconfiguredCount, 'needs', 'need')} to be configured.`
    )
  }

}


function validateTerms(terms) {
  return (
    validateRequiredField(terms, 'electronicSignature') &&
    validateRoot(terms, terms.agreedToTerms, 'Please read and agree to the terms')
  )
}


function validateCreditInfo(credit) {
  validateRequiredField(credit, 'cardName')
  validateMonthNumber(credit, 'cardMonth')
  validateYearNumber(credit, 'cardYear')
  validateRoot(credit, credit.cardNumValid, 'Card Number is invalid')
}


function validateRoot(object, isValid, errorMessage) {
  if (isValid) return true
  object._errors._root = errorMessage
}


const VALID_MONTHS = /^(0[1-9]|1[0-2])$/
function validateMonthNumber(object, fieldName) {
  if (!isPresent(object[fieldName])) {
    object._errors[fieldName] = 'is required'
  } else if (!VALID_MONTHS.test(object[fieldName])) {
    object._errors[fieldName] = 'should be a 2-digit number'
  } else {
    return true
  }
}


const VALID_YEARS = /^[2-9]\d{3}$/
function validateYearNumber(object, fieldName) {
  const val = object[fieldName]
  if (!isPresent(val)) {
    object._errors[fieldName] = 'is required'
  } else if (!VALID_YEARS.test(val)) {
    object._errors[fieldName] = 'should be a 4-digit number'
  } else if (parseFloat(val) < (new Date()).getFullYear()) {
    object._errors[fieldName] = 'should not be in the past'
  } else {
    return true
  }
}


function validateRequiredField(object, fieldName) {
  if (!isPresent(object[fieldName])) {
    object._errors[fieldName] = 'is required'
  } else {
    return true
  }
}


function validateCurrency(object, fieldName) {
  let val = object[fieldName]
  if (!isPresent(val)) return (object._errors[fieldName] = 'is required')
  // Remove the currency symbol (if they used it) -- Books no likey
  if (val.indexOf('$') >= 0) {
    val = val.replace('$', '')
    object[fieldName] = val // Copy back in, to remove the currency symbol
  }
  if (!validator.isCurrency(val, {digits_after_decimal: [1,2,3]})) return (object._errors[fieldName] = 'is invalid')
  return true
}


function validateEmail(object, fieldName) {
  const email = object[fieldName]
  if (!isPresent(email)) return (object._errors[fieldName] = 'is required')
  if (!validator.isEmail(email)) return (object._errors[fieldName] = 'is not a valid address')
  return true
}


function validatePhone(object, fieldName) {
  const val = object[fieldName]
  if (!isPresent(val)) return (object._errors[fieldName] = 'is required')
  if (!validator.isMobilePhone(val)) return (object._errors[fieldName] = 'is invalid')
  return true
}


const VALID_STATE_OR_PROVINCE = /^[a-z]{2}$/i
function validateStateField(object, fieldName) {
  const val = object[fieldName]
  if (!isPresent(val)) return (object._errors[fieldName] = 'is required')
  if (!VALID_STATE_OR_PROVINCE.test(val)) return object._errors[fieldName] = 'should just be a 2-letter abbreviation'
  return true
}


const VALID_COUNTRY = /^[a-z]{2}$/i
function validateCountryField(object, fieldName) {
  const val = object[fieldName]
  if (!isPresent(val)) return (object._errors[fieldName] = 'is required')
  if (!VALID_COUNTRY.test(val)) return object._errors[fieldName] = 'should just be a 2-letter abbreviation (like: US or CA)'
  return true
}


function validateCustomMarkup(object, fieldName) {
  warn('TODO: Implement the validateCustomMarkup')
  return true
}


function validateLengthMinMax(object, fieldName, min, max) {
  const val = compact(object[fieldName])
  if (val.length < min) return (object._errors[fieldName] = 'must have at least '    + min + ' item' + (min === 1 ? '' : 's'))
  if (val.length > max) return (object._errors[fieldName] = 'cannot have more than ' + max + ' item' + (max === 1 ? '' : 's'))
  return true
}


function validatePercentage(object, fieldName) {
  let val = object[fieldName]
  if (!isPresent(val)) return (object._errors[fieldName] = 'is required')
  if (!validator.isFloat(val, {min: 0, max: 100})) return (object._errors[fieldName] = 'is invalid')
  if (!validator.isDecimal(val, {force_decimal: false, decimal_digits: '0,2'})) return (object._errors[fieldName] = 'is invalid')
  return true
}


function isPresent(val) {
  return !!(val || '').trim()
}


function validateOrder(order, opts) {
  const pricingMode = opts.pricingMode

  validateRequiredField(order, 'pricingFormat')
  validateRequiredField(order, 'partsTaxCalculation')
  validateRequiredField(order, 'partsMarkupOption')

  if (order.partsMarkupOption === 'Option 6') {
    validateCustomMarkup(order, 'partsMarkupTable')
  }

  if (order.tradeCode === 'HVC' && pricingMode === 'Legacy') {
    validateLengthMinMax(order, 'oems', 2, 3)
  }

  if (pricingMode !== 'Simplified') {
    validateCurrency(order, 'regularLaborRate')
    validateCurrency(order, 'overtimeLaborRate')
    validateCurrency(order, 'regularDiscountAmount')
  }
  validateRequiredField(order, 'regularDiscountType')

  // Agreement savings does not have an overtime discount
  if (order.pricingFormat !== 'Agreement Savings') {
    if (pricingMode !== 'Simplified') {
      validateCurrency(order, 'overtimeDiscountAmount')
    }
    validateRequiredField(order, 'overtimeDiscountType')
  }

  validatePercentage(order, 'laborTaxRate')
  validatePercentage(order, 'partsTaxRate')
}



//------------------------------------------------------------------------------


let nextOrderId = 1

class Order {

  constructor (tradeCode, market) {
    this.id                     = nextOrderId++
    this.tradeCode              = tradeCode
    this.market                 = market
    this.printedName            = 'Company Name'
    this.pricingFormat          = 'Agreement Reg-OT'
    this.partsTaxCalculation    = ''
    this.partsMarkupOption      = 'Option 2'
    this.partsMarkupTable       = ''
    this.regularFlatAdd         = '2.00'
    this.overtimeFlatAdd        = '2.00'
    this.regularLaborRate       = ''
    this.overtimeLaborRate      = ''
    this.regularDiscountAmount  = ''
    this.regularDiscountType    = '%'
    this.overtimeDiscountAmount = ''
    this.overtimeDiscountType   = '%'
    this.laborTaxRate           = ''
    this.partsTaxRate           = ''
    this.numberOfPrintedBooks   = 0
    this.numberOfLicenses       = 1
    this.bookSelected           = false
    this.displayBarcodes        = false
    this.roundedPrices          = true
    this.displayTime            = true

    this.oems                   = []

    this.printedGroupCodes      = {}

    this.specialRequests        = ''

    this.setDefaultColumns()
  }


  isPrintingGroupCode = (code) => {
    return !!this.printedGroupCodes[code]
  }


  setRepairGroupPrintFlag = (code, printed) => {
    this.printedGroupCodes[code] = printed
  }

  getTradeName = () => {
    if (this.tradeCode === 'HVC') return 'HVAC'
    if (this.tradeCode === 'ELE') return 'Electrical'
    if (this.tradeCode === 'PLB') return 'Plumbing'
    if (this.tradeCode === 'CET') return 'Power Cleaning'
  }

  getTitle = () => {
    let title = this.getTradeName()
    if (this.tradeCode === 'HVC') title += " " + this.market
    return title
  }

  setAllOptionalRepairGroups = (enabled) => {
    if (this.tradeCode !== 'HVC') return
    var order = this
    OPTIONAL_REPAIR_GROUPS.forEach(function(g) {
      order.setRepairGroupPrintFlag(g.code, enabled)
    })
  }

  setDefaultColumns = () => {

    this.column1Highlighted     = false
    this.column2Highlighted     = false
    this.column3Highlighted     = false
    this.column4Highlighted     = false

    this.column1Printed         = true
    this.column2Printed         = true
    this.column3Printed         = true
    this.column4Printed         = true

    if (this.pricingFormat === "Agreement Savings") {
      this.column1Title = "Repair\nFee"
      this.column2Title = "After\nHours"
      this.column3Title = "Service\nAgreement"
      this.column4Title = "Agreement\nSavings"
    }
    else if (this.pricingFormat === "Agreement Reg-OT") {
      this.column1Title = "Repair\nFee"
      this.column2Title = "Service\nAgreement"
      this.column3Title = "After\nHours"
      this.column4Title = "After Hours\nAgreement"
    }
    else if (this.pricingFormat === "Primary Additional") {
      this.column1Title = "Regular\nPrimary"
      this.column2Title = "Regular\nAdditional"
      this.column3Title = "Overtime\nPrimary"
      this.column4Title = "Overtime\nAdditional"
    }
  }

}


const OEMS = [
  { code: 'ARM', name: 'Armstrong' },
  { code: 'BUR', name: 'Burnham - U.S. Boiler' },
  { code: 'CAR', name: 'Carrier/Bryant' },
  { code: 'DAK', name: 'Daikin Industries' },
  { code: 'EMP', name: 'Empire' },
  { code: 'EKI', name: 'Energy Kinectics' },
  { code: 'GOO', name: 'Goodman/Amana' },
  { code: 'ICP', name: 'ICP/Heil/Tempstar/Comfortmaker' },
  { code: 'LNX', name: 'Lennox Industries' },
  { code: 'MIT', name: 'Mitsubishi Electric' },
  { code: 'NDY', name: 'Nordyne/Intertherm/Miller/Gibson' },
  { code: 'REZ', name: 'Reznor' },
  { code: 'RHE', name: 'Rheem/Ruud/Comfort Air' },
  { code: 'TRA', name: 'Trane/American Standard' },
  { code: 'WTR', name: 'Water Furnace' },
  { code: 'YOR', name: 'York/Coleman' },
  { code: 'LOC', name: 'Lochinvar' },
]


const INCLUDED_REPAIR_GROUPS = [
  // { code: "WAR", name: "WARRANTY REPAIRS"}, // Not included in the flash ordering app
  { code: "AIF", name: "AIR FILTER REPAIRS"},
  { code: "AIR", name: "AIR HANDLER REPAIRS"},
  { code: "BLO", name: "BLOWER REPAIRS"},
  { code: "COM", name: "COMPRESSOR REPAIRS"},
  { code: "COD", name: "CONDENSATE REPAIRS"},
  { code: "CAP", name: "CONDENSING, HEAT PUMP & PACKAGE UNIT REPAIRS"},
  { code: "CON", name: "CONTROL REPAIRS"},
  { code: "DUC", name: "DUCTWORK REPAIRS"},
  { code: "ELE", name: "ELECTRICAL REPAIRS"},
  { code: "GAS", name: "GAS HEATING REPAIRS"},
  { code: "HUM", name: "HUMIDIFIER & EVAPORATIVE COOLER REPAIRS"},
  { code: "LEA", name: "LEAK SEARCH REPAIRS"},
  { code: "MIS", name: "MISCELLANEOUS CHARGES"},
  { code: "MOT", name: "MOTOR REPAIRS"},
  { code: "PIP", name: "PIPING REPAIRS"},
  { code: "REF", name: "REFRIGERANT REPAIRS"},
  { code: "RES", name: "REFRIGERATION SPECIALTY REPAIRS"},
  { code: "VEN", name: "VENTING REPAIRS"},
]

const OPTIONAL_REPAIR_GROUPS = [
  { code: "HYD", name: "HYDRONIC REPAIRS"},
  { code: "OIL", name: "OIL HEATING REPAIRS"},
  { code: "PRO", name: "PROPANE REPAIRS"},
  { code: "WAT", name: "WATER HEATER REPAIRS"},
]


const MARKUP_TABLES = [
  {
    value: 'Option 1',
    name: 'Aggressive',
    table: [
      { min:    "0.0",   max:      "2.0",   multiplier: "5.0"  },
      { min:   "2.01",   max:      "5.0",   multiplier: "4.0"  },
      { min:   "5.01",   max:     "10.0",   multiplier: "3.5"  },
      { min:  "10.01",   max:     "20.0",   multiplier: "3.0"  },
      { min:  "20.01",   max:     "40.0",   multiplier: "2.75" },
      { min:  "40.01",   max:     "60.0",   multiplier: "2.5"  },
      { min:  "60.01",   max:    "100.0",   multiplier: "2.25" },
      { min: "100.01",   max:    "150.0",   multiplier: "2.0"  },
      { min: "150.01",   max: "99999.99",   multiplier: "1.75" },
    ]
  },

  {
    value: 'Option 2',
    name: 'Moderate',
    table: [
      { min:    "0.0",   max:      "2.0",   multiplier: "4.0"  },
      { min:   "2.01",   max:      "5.0",   multiplier: "3.25" },
      { min:   "5.01",   max:     "10.0",   multiplier: "3.0"  },
      { min:  "10.01",   max:     "20.0",   multiplier: "2.75" },
      { min:  "20.01",   max:     "40.0",   multiplier: "2.5"  },
      { min:  "40.01",   max:     "60.0",   multiplier: "2.25" },
      { min:  "60.01",   max:    "100.0",   multiplier: "2.0"  },
      { min: "100.01",   max:    "150.0",   multiplier: "1.75" },
      { min: "150.01",   max: "99999.99",   multiplier: "1.65" },
    ]
  },

  {
    value: 'Option 3',
    name: 'Conservative',
    table: [
      { min:    "0.0",   max:      "2.0",   multiplier: "3.5"  },
      { min:   "2.01",   max:      "5.0",   multiplier: "3.0"  },
      { min:   "5.01",   max:     "10.0",   multiplier: "2.75" },
      { min:  "10.01",   max:     "20.0",   multiplier: "2.5"  },
      { min:  "20.01",   max:     "40.0",   multiplier: "2.25" },
      { min:  "40.01",   max:     "60.0",   multiplier: "2.0"  },
      { min:  "60.01",   max:    "100.0",   multiplier: "1.75" },
      { min: "100.01",   max:    "150.0",   multiplier: "1.65" },
      { min: "150.01",   max: "99999.99",   multiplier: "1.5"  },
    ]
  },
]


export {
  OEMS,
  MARKUP_TABLES,
  INCLUDED_REPAIR_GROUPS,
  OPTIONAL_REPAIR_GROUPS,
}



// Return the value of a query param: getQueryParam('foo') => 'bar', if the url
// was currently something like http://example.com/thing?foo=bar&go=forit
// Will be undefined if the param does not exist.
function getQueryParam(variable) {
  var query = window.location.search.substring(1)
  var vars = query.split('&')
  for (var i = 0; i < vars.length; i++) {
    var pair = vars[i].split('=')
    if (decodeURIComponent(pair[0]) === variable) {
      return decodeURIComponent(pair[1])
    }
  }
}


// Converts "billingAddress" into "Billing address"
const FIRST_LETTERS = /(^.|[A-Z])/g
function humanize(s) {
  s = s.replace(FIRST_LETTERS, ' $1').trim() // Insert spaces
  if (s.length > 0) {
    s = s[0].toUpperCase() + s.substr(1).toLowerCase() // Capitalize first word
  }
  return s
}


function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
